<template>
  <AuthLayout :title="title" :subtitle="subtitle">
    <!-- Checking the link -->
    <p v-if="checking" role="status" class="text-muted italic">
      {{ $t("loading") }}
    </p>

    <!-- Bad or expired link: a dead end unless we offer the way out. -->
    <div v-else-if="linkError" class="flex flex-col gap-4">
      <p role="alert" class="text-red-400">{{ linkError }}</p>
      <router-link
        to="/forgot-password"
        class="text-highlight text-sm hover:underline self-start"
      >
        {{ $t("auth.reset.requestNew") }}
      </router-link>
    </div>

    <!-- Done -->
    <div v-else-if="done" role="status" class="flex flex-col gap-4">
      <p class="text-muted leading-relaxed">{{ $t("auth.reset.doneBody") }}</p>
      <router-link
        to="/login"
        class="text-highlight text-sm hover:underline self-start"
      >
        {{ $t("auth.reset.signIn") }}
      </router-link>
    </div>

    <form v-else class="flex flex-col gap-5" @submit.prevent="submit">
      <!-- Shown, not editable: it tells the user which account they are
           changing, and a password manager needs it to save against. -->
      <InputField
        v-if="email"
        :value="email"
        :label="$t('account.email')"
        type="email"
        name="email"
        autocomplete="username"
        readonly
      />
      <div>
        <InputField
          v-model="password"
          :label="$t('auth.reset.newPassword')"
          type="password"
          name="password"
          autocomplete="new-password"
          required
        />
        <p class="text-subtle text-xs mt-1.5">
          {{ $t("auth.register.passwordRule", { count: minLength }) }}
        </p>
      </div>
      <InputField
        v-model="confirm"
        :label="$t('account.confirmPassword')"
        type="password"
        name="password_confirmation"
        autocomplete="new-password"
        required
      />

      <Button class="mt-2 w-full text-lg" :loading="saving">
        {{ $t("auth.reset.submit") }}
      </Button>
      <ErrorLabel :error="errorMessage" />
    </form>
  </AuthLayout>
</template>

<script>
import AuthLayout from "@/components/auth/AuthLayout.vue";
import Button from "@/components/common/Button.vue";
import ErrorLabel from "@/components/common/ErrorLabel.vue";

const MIN_PASSWORD_LENGTH = 6;

export default {
  name: "ResetPassword",
  components: { AuthLayout, Button, ErrorLabel },

  data() {
    return {
      checking: true,
      saving: false,
      done: false,
      email: "",
      // True when the account has never been verified — the same link then
      // doubles as "set your first password".
      firstTime: false,
      password: "",
      confirm: "",
      errorMessage: "",
      linkError: "",
      minLength: MIN_PASSWORD_LENGTH,
    };
  },

  computed: {
    params() {
      const { hash, key, token } = this.$route.params;
      return { hash, userId: key, token };
    },
    title() {
      if (this.done) return this.$t("auth.reset.doneTitle");
      return this.firstTime
        ? this.$t("auth.reset.firstTimeTitle")
        : this.$t("auth.reset.title");
    },
    subtitle() {
      if (this.checking || this.done || this.linkError) return "";
      return this.firstTime
        ? this.$t("auth.reset.firstTimeSubtitle")
        : this.$t("auth.reset.subtitle");
    },
  },

  mounted() {
    // Validate before showing the form, so a dead link fails immediately
    // instead of after the user has typed a new password twice.
    this.$store
      .dispatch("verify", this.params)
      .then((data) => {
        this.email = data?.email ?? "";
        this.firstTime = data?.wasAlreadyVerified === false;
      })
      .catch(() => {
        this.linkError = this.$t("auth.error.invalidToken").toString();
      })
      .finally(() => (this.checking = false));
  },

  methods: {
    validate() {
      if (this.password.length < MIN_PASSWORD_LENGTH) {
        return this.$t("auth.error.passwordValidation").toString();
      }
      if (this.password !== this.confirm) {
        return this.$t("auth.error.passwordMatch").toString();
      }
      return "";
    },

    submit() {
      if (this.saving) return;

      const validationError = this.validate();
      if (validationError) {
        this.errorMessage = validationError;
        return;
      }

      this.saving = true;
      this.errorMessage = "";

      this.$store
        .dispatch("setPassword", {
          userId: this.params.userId,
          formData: {
            token: this.params.token,
            password: this.password,
            password_confirmation: this.confirm,
          },
        })
        .then(() => {
          this.done = true;
          this.password = "";
          this.confirm = "";
        })
        .catch((error) => {
          // 403 means the token was spent or expired between loading the page
          // and submitting — that needs a fresh link, not a retry.
          if (error?.response?.status === 403) {
            this.linkError = this.$t("auth.error.invalidToken").toString();
          } else {
            this.errorMessage = this.$t("auth.error.passwordUpdate").toString();
          }
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>
